package com.isaias.fyattravel.web.pages.explore

import androidx.compose.runtime.Composable
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

private val NEAR_BLACK = Color("#141210")

/**
 * "Explore Uganda Safari" — Uganda safari is the main event, Dubai is a small
 * optional add-on. Single scrollable page, no pricing anywhere.
 */
@Composable
fun ExplorePage() {
    Div {
        HeroSection()
        ImageShowcaseSection()
        PackagesSection()
        DubaiAddOnSection()
        TrustSection()
        FinalCtaBand()
    }
}

@Composable
private fun HeroSection() {
    Div({
        classes("explore-hero")
        style {
            position(Position.Relative)
            overflow("hidden")
            backgroundColor(NEAR_BLACK)
            color(Color("#FFFFFF"))
            // Full-bleed: break out of the page's centered max-width column.
            // The content column already has overflow-x:hidden, so this is
            // safely clipped to its real edges on every screen size.
            width(100.vw)
            property("margin-left", "calc(50% - 50vw)")
            property("margin-right", "calc(50% - 50vw)")
            property("border-radius", "0 0 3em 3em")
            padding(4.5.em, 3.em, 4.em, 3.em)
            property("text-align", "left")
        }
    }) {
        ExploreHeroBackground()

        // Dark scrim over the rotating photo so headline/body text stays legible
        Div({
            style {
                position(Position.Absolute)
                property("inset", "0")
                property(
                    "background",
                    "linear-gradient(180deg, rgba(20,18,16,0.72) 0%, rgba(20,18,16,0.55) 45%, rgba(20,18,16,0.82) 100%)"
                )
            }
        })

        Div({
            style {
                position(Position.Relative)
                maxWidth(520.px)
            }
        }) {
            Span({
                style {
                    display(DisplayStyle.InlineBlock)
                    fontSize(0.72.em)
                    fontWeight("600")
                    letterSpacing(0.28.em)
                    property("text-transform", "uppercase")
                    color(Color("#E7C9A0"))
                    marginBottom(0.9.em)
                }
            }) { Text("The Pearl of Africa") }

            H1({
                classes("explore-hero-title")
                style {
                    fontWeight("700")
                    property("font-style", "italic")
                    letterSpacing((-0.01).em)
                    margin(0.px, 0.px, 0.6.em, 0.px)
                    lineHeight("1.08")
                }
            }) { Text("Uganda Safari, the Way It's Meant to Be Seen") }

            P({
                style {
                    fontSize(0.98.em)
                    color(Color("#D9D4CC"))
                    margin(0.px, 0.px, 1.8.em, 0.px)
                    lineHeight("1.6")
                }
            }) {
                Text(
                    "Gorilla trekking, big cats and the source of the Nile — Uganda is the " +
                        "main journey. A short Dubai stopover is available as an optional add-on."
                )
            }

            Div({
                style {
                    property("display", "flex")
                    flexWrap(FlexWrap.Wrap)
                    justifyContent(JustifyContent.FlexStart)
                    gap(1.em)
                }
            }) {
                A(
                    href = waLink("Hi! I'd like to know more about Uganda safari packages."),
                    attrs = {
                        attr("target", "_blank")
                        attr("rel", "noopener noreferrer")
                        style {
                            property("display", "inline-flex")
                            alignItems(AlignItems.Center)
                            backgroundColor(Color("#AE7930"))
                            color(Color("#FFFFFF"))
                            fontWeight("700")
                            fontSize(0.95.em)
                            padding(0.9.em, 1.6.em)
                            borderRadius(0.7.em)
                            textDecoration("none")
                            cursor("pointer")
                        }
                    }
                ) { Text("Enquire on WhatsApp") }

                A(
                    attrs = {
                        attr("href", "#packages")
                        onClick { event ->
                            event.preventDefault()
                            document.getElementById("packages")
                                ?.asDynamic()
                                ?.scrollIntoView(js("({behavior: 'smooth', block: 'start'})"))
                        }
                        style {
                            property("display", "inline-flex")
                            alignItems(AlignItems.Center)
                            color(Color("#FFFFFF"))
                            fontWeight("600")
                            fontSize(0.95.em)
                            padding(0.9.em, 1.6.em)
                            borderRadius(0.7.em)
                            border(1.5.px, LineStyle.Solid, Color("#5A544A"))
                            textDecoration("none")
                            cursor("pointer")
                        }
                    }
                ) { Text("See the Packages") }
            }
        }
    }
}

@Composable
private fun TrustSection() {
    val points = listOf(
        "Local Uganda-based guides" to "Every trek and drive is led by guides who know the parks firsthand.",
        "Direct WhatsApp communication" to "No call centers — talk straight to the people planning your trip.",
        "Tailored itineraries" to "Every package above is a starting point; we adjust it to your dates and group."
    )
    Div({
        style {
            padding(2.5.em, 1.5.em)
            property("margin", "0 auto")
            maxWidth(1000.px)
        }
    }) {
        Div({
            style {
                property("display", "grid")
                property("grid-template-columns", "repeat(auto-fit, minmax(220px, 1fr))")
                gap(1.5.em)
            }
        }) {
            points.forEach { (title, body) ->
                Div {
                    Div({
                        style {
                            fontSize(0.95.em)
                            fontWeight("800")
                            color(NEAR_BLACK)
                            marginBottom(0.3.em)
                        }
                    }) { Text(title) }
                    Div({
                        style {
                            fontSize(0.85.em)
                            color(Color("#555555"))
                            lineHeight("1.5")
                        }
                    }) { Text(body) }
                }
            }
        }
    }
}

@Composable
private fun FinalCtaBand() {
    Div({
        style {
            backgroundColor(NEAR_BLACK)
            color(Color("#FFFFFF"))
            padding(3.5.em, 1.5.em)
            property("text-align", "center")
        }
    }) {
        H2({
            style {
                fontWeight("800")
                fontSize(1.6.em)
                margin(0.px, 0.px, 1.1.em, 0.px)
            }
        }) { Text("Ready to plan your Uganda safari?") }

        A(
            href = waLink("Hi! I'm ready to start planning my Uganda safari."),
            attrs = {
                attr("target", "_blank")
                attr("rel", "noopener noreferrer")
                style {
                    property("display", "inline-flex")
                    alignItems(AlignItems.Center)
                    backgroundColor(Color("#AE7930"))
                    color(Color("#FFFFFF"))
                    fontWeight("700")
                    fontSize(1.05.em)
                    padding(1.em, 2.2.em)
                    borderRadius(0.8.em)
                    textDecoration("none")
                    cursor("pointer")
                }
            }
        ) { Text("Message Us on WhatsApp") }
    }
}
